import { useCallback } from 'react';
import noop from 'lodash/noop';
import { isServer, isWorker } from '@frameio/utils/config';
import { log } from '@frameio/utils/log';
import { datadogLogger } from './datadog/logger';

const measurements = '__performanceMeasurements';
if (!isServer && !isWorker) {
  // eslint-disable-next-line dot-notation
  (window as any)[measurements] = ((window as any)[measurements] ||
    {}) as MeasurementStorage;
}

type Measurement = {
  beginTime: number;
  timeoutRef?: ReturnType<typeof setTimeout>;
};

type MeasurementStorage = Record<string, Measurement>;

type MeasurementName =
  | 'access_requests_panel_open'
  | 'comment_edit_ready'
  | 'notifications_sheet_open'
  | 'page_navigation_from_notification'
  | 'resolution_change_ready'
  | 'video_mounted_canplay_ready'
  | 'viewer_first_render_to_ready';

/**
 * Measures time taken for a given operation. And reports the result to DataDog.
 * @param measurementName
 * @param timeout
 */
export const usePerformanceMeasure = (
  measurementName: MeasurementName,
  timeout = 20000
) => {
  // Clear the measurement, optionally log a warning for implementation errors
  const clear = useCallback(
    (warningMsg?: string) => {
      // eslint-disable-next-line dot-notation
      const m: MeasurementStorage = window[measurements];
      const timeoutRef = m[measurementName]?.timeoutRef;
      if (timeoutRef) clearTimeout(timeoutRef);
      delete m[measurementName];
      if (warningMsg) {
        log.warn(`[usePerformanceMeasure] ${warningMsg}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [measurementName]
  );

  // Start the timer for this measurement
  const begin = useCallback(() => {
    // eslint-disable-next-line dot-notation
    const m: MeasurementStorage = window[measurements];
    const beginTime: number = performance?.now();
    const timeoutRef = setTimeout(clear, timeout);
    m[measurementName] = { beginTime, timeoutRef };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurementName, timeout]);

  const end = useCallback(() => {
    const endTime: number = performance?.now();
    // eslint-disable-next-line dot-notation
    const m: MeasurementStorage = window[measurements];
    const measurement = m[measurementName];

    // If end() is called, but begin() was never called, do nothing
    if (!measurement) return;
    const beginTime: number = m[measurementName]?.beginTime;

    if (typeof beginTime !== 'number' || typeof endTime !== 'number') {
      clear(
        `Invalid measurements for '${measurementName}' — begin: ${beginTime}, end: ${endTime}`
      );
      return;
    }
    if (isNaN(beginTime) || isNaN(endTime)) {
      clear(
        `Invalid measurements for '${measurementName}' — begin: ${beginTime}, end: ${endTime}`
      );
      return;
    }
    if (endTime < beginTime) {
      clear(`endTime occurred before beginTime for '${measurementName}'`);
      return;
    }
    if (endTime === beginTime) {
      clear(`beginTime and endTime are the same for '${measurementName}'`);
      return;
    }

    const elapsedTime = endTime - beginTime;
    // timeout is included because the setTimeout should have cleared the measurement
    if (isNaN(elapsedTime) || elapsedTime < 0 || elapsedTime > timeout) {
      clear(
        `Invalid measurements for '${measurementName}' — begin: ${beginTime}, end: ${endTime}`
      );
      return;
    }

    clear();

    log.debug(`[usePerformanceMeasure] '${measurementName}' ${elapsedTime}ms`);

    datadogLogger.log('performanceMeasurement', {
      measurementName,
      measurementTime: elapsedTime,
    });
  }, [measurementName, timeout, clear]);

  if (isServer) {
    return {
      beginMeasurement: noop,
      endMeasurement: noop,
      clearMeasurement: noop,
    };
  }

  return {
    beginMeasurement: begin,
    endMeasurement: end,
    clearMeasurement: clear,
  };
};
